import { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Lock, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/utils/api';

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  if (!token) {
    return (
      <div data-testid="reset-password-page" className="pb-nav flex items-center justify-center min-h-[70vh] px-4">
        <div className="text-center">
          <div className="w-14 h-14 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center mx-auto mb-4">
            <AlertTriangle size={24} className="text-red-400" />
          </div>
          <h2 className="text-lg font-bold">Enlace inválido</h2>
          <p className="text-sm text-zinc-500 mt-2">Este enlace no es válido. Solicitá uno nuevo.</p>
          <Link to="/forgot-password" className="inline-block mt-4 text-sm text-[#E84C1C] hover:underline">Solicitar nuevo enlace</Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) { setError('Las contraseñas no coinciden.'); return; }
    if (password.length < 6) { setError('Mínimo 6 caracteres.'); return; }

    setLoading(true);
    try {
      await api.post('/auth/reset-password', { token, newPassword: password });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al restablecer la contraseña.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="reset-password-page" className="pb-nav flex items-center justify-center min-h-[70vh] px-4">
      <div className="w-full max-w-sm">
        {!success ? (
          <>
            <div className="w-14 h-14 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center mb-4">
              <Lock size={24} className="text-[#E84C1C]" />
            </div>
            <h1 className="text-xl font-bold">Nueva contraseña</h1>
            <p className="text-sm text-zinc-500 mt-1 mb-6">Elegí una nueva contraseña para tu cuenta.</p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-zinc-400 text-xs">Nueva contraseña</Label>
                <Input
                  data-testid="reset-password-input"
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Min. 6 caracteres"
                  required
                  minLength={6}
                  className="bg-zinc-900 border-zinc-800 h-11 mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Confirmar contraseña</Label>
                <Input
                  data-testid="reset-confirm-input"
                  type="password"
                  value={confirm}
                  onChange={e => setConfirm(e.target.value)}
                  placeholder="Repetí la contraseña"
                  required
                  minLength={6}
                  className="bg-zinc-900 border-zinc-800 h-11 mt-1"
                />
              </div>

              {error && <p data-testid="reset-error" className="text-red-400 text-sm">{error}</p>}

              <button
                data-testid="reset-submit"
                type="submit"
                disabled={loading}
                className="w-full bg-[#E84C1C] text-white font-medium py-3 rounded-full hover:bg-[#d43f12] active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading && <Loader2 size={18} className="animate-spin" />}
                Restablecer contraseña
              </button>
            </form>
          </>
        ) : (
          <div className="text-center" data-testid="reset-success">
            <div className="w-14 h-14 rounded-full bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={24} className="text-emerald-400" />
            </div>
            <h2 className="text-lg font-bold">Contraseña actualizada</h2>
            <p className="text-sm text-zinc-400 mt-2">Tu contraseña fue restablecida exitosamente. Ya podés iniciar sesión.</p>
            <Link to="/" data-testid="reset-go-login" className="inline-block mt-6 bg-[#E84C1C] text-white font-medium px-8 py-3 rounded-full hover:bg-[#d43f12] transition-all">
              Iniciar sesión
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
